/**
 * Auth-bootstrap network layer. These calls deliberately use raw `fetch` (not the
 * apiClient interceptor) because they run the auth bootstrap itself — the token
 * they carry is not yet in the interceptor's reach. Kept in one module so the
 * localStorage/fetch-Authorization lint guards are relaxed only here (see
 * eslint.config.js), not across the app.
 */
import { AuthError } from './AuthContext';
import type {
  AuthErrorCode,
  AuthTokens,
  LoginCredentials,
  RegisterData,
  RegistrationCapability,
  User,
} from './AuthContext';
import { resolveBrowserServiceEndpoints } from '@/api/baseUrl';

// Service base URLs. Constant per session (derived from Vite env), so they live
// at module scope and never enter a React dependency array.
const {
  apiBaseUrl: API_BASE_URL,
  authBaseUrl: AUTH_BASE_URL,
} = resolveBrowserServiceEndpoints({
  isDevelopment: import.meta.env.DEV,
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL,
  authBaseUrl: import.meta.env.VITE_AUTH_BASE_URL,
  djangoApiBaseUrl: import.meta.env.VITE_DJANGO_API_BASE_URL,
});

export async function fetchRegistrationCapability(): Promise<RegistrationCapability> {
  const response = await fetch(
    `${API_BASE_URL}/api/users/auth/registration-capability/`,
  );
  if (!response.ok) {
    throw new Error(`Registration capability returned ${response.status}`);
  }
  const payload: unknown = await response.json();
  if (
    typeof payload !== 'object' ||
    payload === null ||
    !['closed', 'invite', 'open'].includes(
      String((payload as Record<string, unknown>).mode),
    ) ||
    typeof (payload as Record<string, unknown>).registration_available !== 'boolean' ||
    typeof (payload as Record<string, unknown>).invitation_required !== 'boolean'
  ) {
    throw new Error('Registration capability contract is invalid');
  }
  return payload as RegistrationCapability;
}

/** POST credentials → access/refresh tokens. Maps server errors to AuthError. */
export async function requestTokens(credentials: LoginCredentials): Promise<AuthTokens> {
  const response = await fetch(`${AUTH_BASE_URL}/api/users/auth/token/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const detail = typeof errorData?.detail === 'string' ? errorData.detail : '';
    const lower = detail.toLowerCase();
    let code: AuthErrorCode;
    if (
      response.status === 401 ||
      lower.includes('no active account') ||
      lower.includes('credentials')
    ) {
      code = 'INVALID_CREDENTIALS';
    } else {
      code = 'LOGIN_FAILED';
    }
    throw new AuthError(code, detail || 'Login failed');
  }

  const tokenData = await response.json();
  return {
    access: tokenData.access,
    refresh: tokenData.refresh,
  };
}

/** POST refresh token → rotated tokens. Some backends rotate the refresh token. */
export async function requestTokenRefresh(refreshToken: string): Promise<AuthTokens> {
  const response = await fetch(`${AUTH_BASE_URL}/api/users/auth/token/refresh/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh: refreshToken }),
  });

  if (!response.ok) {
    throw new Error('Token refresh failed');
  }

  const data = await response.json();
  return {
    access: data.access,
    refresh: data.refresh || refreshToken, // Some implementations rotate refresh tokens
  };
}

/**
 * Fetch the user profile during login. NO FALLBACK: a synthetic user would
 * silently corrupt export metadata, audit trails and per-tier display, so a
 * non-OK response throws and the caller fails the login.
 */
export async function fetchUserProfileForLogin(accessToken: string): Promise<User> {
  const profileResponse = await fetch(`${API_BASE_URL}/api/v1/user-forests/profile/`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });

  if (!profileResponse.ok) {
    throw new AuthError(
      'PROFILE_FETCH_FAILED',
      `Profile fetch returned ${profileResponse.status}`,
    );
  }
  return profileResponse.json();
}

/**
 * Raw profile fetch used by the cold-load bootstrap, which must inspect the
 * status (401 → refresh-or-logout) rather than throw. Returns the Response.
 */
export function fetchUserProfileResponse(accessToken: string): Promise<Response> {
  return fetch(`${API_BASE_URL}/api/v1/user-forests/profile/`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
}

function registrationErrorCode(errorCode: string, message: string): AuthErrorCode {
  const lower = message.toLowerCase();
  if (errorCode === 'registration_closed') return 'REGISTRATION_CLOSED';
  if (errorCode === 'registration_invitation_required') {
    return 'REGISTRATION_INVITATION_REQUIRED';
  }
  if (errorCode === 'registration_invitation_invalid') {
    return 'REGISTRATION_INVITATION_INVALID';
  }
  if (errorCode === 'registration_email_delivery_failed') {
    return 'REGISTRATION_EMAIL_DELIVERY_FAILED';
  }
  if (lower.includes('already exists')) return 'EMAIL_EXISTS';
  if (lower.includes('valid email')) return 'EMAIL_INVALID';
  if (lower.includes('password')) return 'PASSWORD_WEAK';
  if (lower.includes('required')) return 'MISSING_FIELDS';
  return 'REGISTRATION_FAILED';
}

/** POST registration data. Maps server errors to a coded AuthError. */
export async function registerUser(data: RegisterData): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/users/auth/register/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorCode =
      typeof errorData?.error_code === 'string' ? errorData.error_code : '';
    const errMsg =
      typeof errorData?.error === 'string'
        ? errorData.error
        : typeof errorData?.detail === 'string'
          ? errorData.detail
          : '';
    const code = registrationErrorCode(errorCode, errMsg);
    throw new AuthError(code, errMsg || 'Registration failed');
  }
}

/**
 * Best-effort server-side blacklist of the refresh token on logout. Network
 * errors are swallowed so local state cleanup is never blocked.
 */
export function blacklistRefreshToken(refresh: string): void {
  try {
    void Promise.resolve(
      fetch(`${AUTH_BASE_URL}/api/users/auth/logout/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh }),
      }),
    ).catch(() => {
      /* ignore network errors — local state still clears */
    });
  } catch {
    // Best-effort server logout must not block local state cleanup.
  }
}
